import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Address, TextNodeType } from '@mwe/models';
import { DetailsLinkTypeEnum } from '@mwe/constants';
import {
  DetailsLinkTypeEnumExporter,
  getAddressGroupFormatted,
  isAddressGroupToBeDisplayedDisabled,
  isArrayWithMinOneItem,
} from '@mwe/utils';
import { TooltipCardComponent } from '../tooltip-card/tooltip-card.component';
import { NgbDropdown, NgbDropdownButtonItem, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectionCardComponent } from '../cards/selection-card/selection-card.component';
import { InfoboxComponent } from '../infobox/infobox.component';

export interface AddressDropdownItem {
  id: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'mwe-address',
  templateUrl: './address.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TooltipCardComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgClass,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    SelectionCardComponent,
    TranslatePipe,
    InfoboxComponent,
  ],
})
export class AddressComponent extends DetailsLinkTypeEnumExporter {
  addressIdx = input<number | string>();
  dropdownType = input<'pen' | 'dropdown' | 'custom' | 'dots' | string>();
  address = input.required<Address>();
  dropdownEditKey = input<string>();
  allDropdownItems = input<AddressDropdownItem[]>([]); // is split in featuredButtons and dropdownItems
  featuredBtnIds = input<string[]>([]);
  orderDateFormatted = input<string>();
  recentOrderMsgKey = input<string>();
  isArrowDownHidden = input<boolean>(false);
  isAddressDetailsBodyVisible = input<boolean>(false);
  productCategories = input<string[]>([]);
  useRedesignedCard = input(false);
  singleMarker = input(false);
  textNodeType = input<TextNodeType>();
  addressHash = computed(() => this.address()?.hashCode);
  isEndedContract = input<boolean>();
  hideTooltipBody = input<boolean>(false);
  onItemClick = output<string>();
  disabled = computed<boolean>(() =>
    isAddressGroupToBeDisplayedDisabled(this.address().addressGroup.detailsLinkType, this.productCategories()),
  );
  dropdownEditId = computed<string>(() => `mwe-address-${this.addressIdx()}-dropdown`);
  formattedAddress = computed<string>(() => getAddressGroupFormatted(this.address()));
  tooltipClass = computed<{ [value: string]: boolean }>(() => ({
    single: this.singleMarker(),
    'no-after': !!this.orderDateFormatted() || this.isArrowDownHidden(),
    'has-body':
      (!!this.address().addressGroup.detailsLinkType && this.isAddressDetailsBodyVisible()) || this.orderDateFormatted()?.length > 0,
  }));
  detailsLinkItemKey = computed<string>(() => {
    if (this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.RELOCATION) {
      return 'relocation.status';
    }

    if (
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.MOVEOUT ||
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.MOVEOUT_IGNORE_ERROR
    ) {
      return 'moveout.status';
    }

    return '';
  });

  featuredButtons = computed<AddressDropdownItem[]>(() => {
    return this.allDropdownItems()?.filter(i => this.hasId(i.id));
  });

  dropdownItems = computed<AddressDropdownItem[]>(() => {
    if (!isArrayWithMinOneItem(this.featuredBtnIds())) {
      return this.allDropdownItems() || [];
    }

    return this.allDropdownItems().filter(i => !this.hasId(i.id));
  });

  hasTooltipBody = computed(() => {
    return (
      ((this.address().addressGroup.detailsLinkType && this.isAddressDetailsBodyVisible()) || this.orderDateFormatted()?.length > 0) &&
      !this.isEndedContract()
    );
  });

  shouldDisplayActionComponents = computed(() => {
    return !this.isEndedContract() && this.hasDisplayableActionComponents();
  });

  private hasDisplayableActionComponents(): boolean {
    return this.featuredButtons().length > 0 || !!this.dropdownType();
  }

  constructor() {
    super();
  }

  executeItemAction(id: string): void {
    this.onItemClick.emit(id);
  }

  private hasId(id: string) {
    return !!this.featuredBtnIds().find(fId => fId === id);
  }
}
